import PageHero from "@/components/feature/PageHero";
import CtaBanner from "@/components/feature/CtaBanner";
import { contactInfo } from "@/mocks/site";

export interface LegalBlock {
  heading: string;
  paragraphs: string[];
  list?: string[];
}

interface LegalPageProps {
  title: string;
  crumb: string;
  updated: string;
  intro: string;
  blocks: LegalBlock[];
  image?: string;
}

export default function LegalPage({ title, crumb, updated, intro, blocks, image }: LegalPageProps) {
  return (
    <>
      <PageHero
        eyebrow={`Last updated: ${updated}`}
        title={title}
        subtitle={intro}
        crumbs={[{ label: "Home", href: "/" }, { label: crumb }]}
        image={image}
      />

      <section className="bg-background-50 py-14 md:py-16">
        <div className="mx-auto grid max-w-screen-2xl grid-cols-1 gap-10 px-4 md:px-6 lg:grid-cols-[1fr_320px]">
          <article className="space-y-8">
            {blocks.map((block, index) => (
              <div key={block.heading} className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                <h2 className="flex items-start gap-3 font-heading text-lg font-bold text-foreground-950 md:text-xl">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary-500 text-xs font-bold text-background-50">
                    {index + 1}
                  </span>
                  {block.heading}
                </h2>
                <div className="mt-3 space-y-3">
                  {block.paragraphs.map((paragraph) => (
                    <p key={paragraph} className="text-sm leading-relaxed text-foreground-600">
                      {paragraph}
                    </p>
                  ))}
                </div>
                {block.list && (
                  <ul className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {block.list.map((item) => (
                      <li key={item} className="flex items-start gap-2 text-sm text-foreground-700">
                        <i className="ri-checkbox-circle-line mt-0.5 text-primary-500" aria-hidden="true" />
                        {item}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </article>

          <aside className="space-y-5">
            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h3 className="font-heading text-base font-semibold text-foreground-950">Questions?</h3>
              <p className="mt-2 text-sm text-foreground-600">
                Our team is happy to clarify anything about this policy.
              </p>
              <ul className="mt-4 space-y-3 text-sm">
                <li className="flex items-start gap-2 text-foreground-700">
                  <i className="ri-phone-line mt-0.5 text-primary-500" aria-hidden="true" />
                  <a href={contactInfo.phoneHref} className="hover:text-primary-600">{contactInfo.phone}</a>
                </li>
                <li className="flex items-start gap-2 break-all text-foreground-700">
                  <i className="ri-mail-line mt-0.5 text-primary-500" aria-hidden="true" />
                  <a href={contactInfo.emailHref} className="hover:text-primary-600">{contactInfo.email}</a>
                </li>
                <li className="flex items-start gap-2 text-foreground-700">
                  <i className="ri-map-pin-2-line mt-0.5 text-primary-500" aria-hidden="true" />
                  {contactInfo.address}
                </li>
              </ul>
            </div>
          </aside>
        </div>
      </section>

      <CtaBanner
        title="Still have a question about this policy?"
        description="Talk to our Bangalore moving team directly. We are available 24 x 7 for quotes, packing advice and shifting support."
      />
    </>
  );
}